import React from 'react';
import { breakpoints, palette } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { CustomLinkBanner } from 'App/CustomLinkBanner/CustomLinkBanner';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import { Actions } from 'App/Header/Actions';
import { SmallFormTitle } from 'App/Header/SmallFormTitle';
import { Title } from 'App/Header/Title';
import { useLogoCompressionBreakpoint } from 'App/Header/useLogoCompressionBreakpoint';
import { ItemSearchBar } from 'App/Masthead/ItemSearchBar';
import { OfflineBanner } from 'Components/AppLayout';
import { ConditionalRenderer } from 'Components/ConditionalRenderer';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { Banner } from './Banner';
import { useFormFactor } from './useFormFactor';

const StyledContent = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
`;

const StyledAppHeaderContainer = styled.div`
	max-width: ${breakpoints.maxContentWidthPX}px;
	margin: 0 auto;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	width: 100%;
	border: solid ${palette.grey300};
	border-width: 0 0 1px 0;
	overflow: hidden;

	& > *:last-child {
		border-width: 0;
	}

	@media (max-width: ${breakpoints.maxContentWidthPX}px) {
		border-radius: 0;
	}
`;

interface HeaderContentProps {
	isLargeFormFactor: boolean;
}

const CustomLinkSection = () => {
	const isCustomLinkEnabled = useFeatureCanary(FeatureFlag.EnableCustomLinks);
	const customLinkData =
		useConfigurationContext().workspaceConfiguration?.pinnedLinks?.pinnedLink || [];
	const { isSmallFormFactor } = useFormFactor();

	if (!isCustomLinkEnabled || !customLinkData.length) {
		return null;
	}

	return (
		<CustomLinkBanner
			customLinkData={customLinkData}
			isSmallFormFactor={isSmallFormFactor}
		/>
	);
};

const HeaderContent = ({ isLargeFormFactor }: HeaderContentProps) => {
	const { compressedDims } = useLogoCompressionBreakpoint();

	return (
		<>
			{!environment.citrixCloudConnected && <OfflineBanner />}
			<Banner>
				<StyledContent data-testid="header-content">
					{isLargeFormFactor ? (
						<Title compressedDims={compressedDims} />
					) : (
						<SmallFormTitle />
					)}
					{isLargeFormFactor && <ItemSearchBar />}
					<Actions />
				</StyledContent>
				{!isLargeFormFactor && <ItemSearchBar />}
			</Banner>
			{isLargeFormFactor && <CustomLinkSection />}
		</>
	);
};

export const _DaasHeader = () => {
	const isCenterAlignClassicViewEnabled = useFeatureCanary(
		FeatureFlag.CenterAlignClassicView
	);
	const { isLargeFormFactor } = useFormFactor();

	const StyledAppHeaderContainerProps = { 'data-testid': 'app-header-container' };

	return (
		<ConditionalRenderer
			flag={isCenterAlignClassicViewEnabled && isLargeFormFactor}
			OnComponent={StyledAppHeaderContainer}
			OnComponentProps={StyledAppHeaderContainerProps}
		>
			<HeaderContent isLargeFormFactor={isLargeFormFactor} />
		</ConditionalRenderer>
	);
};

export const DaasHeader = () => {
	return (
		<GenericErrorBoundary renderProp={() => <_DaasHeader />} name="DaasHeaderError" />
	);
};
